import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
    FaJava, FaPython, FaJs, FaDatabase, FaServer,
    FaHtml5, FaCss3Alt, FaReact
} from 'react-icons/fa';
import { SiDjango, SiCsharp, SiSpringboot, SiMongodb, SiPostgresql } from 'react-icons/si';

const Skills = ({ darkMode }) => {
    const { t } = useTranslation();
    const [activeCategory, setActiveCategory] = useState('ai');

    // Lista de habilidades con categorías
    const skillsData = {
        languages: [
            { name: "Python", icon: <FaPython />, level: 90 },
            { name: "Java", icon: <FaJava />, level: 85 },
            { name: "JavaScript", icon: <FaJs />, level: 80 },
            { name: "C#", icon: <SiCsharp />, level: 70 },
            { name: "PHP", icon: null, level: 75 },
            { name: "C", icon: null, level: 75 }
        ],
        frontend: [
            { name: "HTML5", icon: <FaHtml5 />, level: 90 },
            { name: "CSS3", icon: <FaCss3Alt />, level: 85 },
            { name: "React", icon: <FaReact />, level: 75 },
            { name: "Bootstrap", icon: null, level: 80 },
            { name: "Tailwind CSS", icon: null, level: 75 }
        ],
        backend: [
            { name: "Django", icon: <SiDjango />, level: 85 },
            { name: "FastAPI", icon: null, level: 80 },
            { name: "Spring Boot", icon: <SiSpringboot />, level: 80 },
            { name: "Laravel", icon: null, level: 70 },
            { name: "Node.js", icon: null, level: 65 }
        ],
        database: [
            { name: "PostgreSQL", icon: <SiPostgresql />, level: 85 },
            { name: "MySQL", icon: <FaDatabase />, level: 85 },
            { name: "MongoDB", icon: <SiMongodb />, level: 80 },
            { name: "SQL Server", icon: <FaDatabase />, level: 75 },
            { name: "Vector DB", icon: null, level: 70 }
        ],
        ai: [
            { name: "LangChain", icon: null, level: 85 },
            { name: "OpenAI API", icon: null, level: 85 },
            { name: "Streamlit", icon: null, level: 85 },
            { name: "LLMs", icon: null, level: 80 },
            { name: "RAG", icon: null, level: 80 },
            { name: "AI Agents", icon: null, level: 75 },
            { name: "Prompt Engineering", icon: null, level: 85 },
            { name: "NLP", icon: null, level: 75 }
        ],
        other: [
            { name: "Git", icon: null, level: 85 },
            { name: "Docker", icon: null, level: 70 },
            { name: "Linux", icon: null, level: 80 },
            { name: "RESTful API", icon: <FaServer />, level: 85 }
        ]
    };

    const categories = [
        { id: 'all', name: t('skills.categories.all') },
        { id: 'ai', name: t('skills.categories.ai') },
        { id: 'languages', name: t('skills.categories.languages') },
        { id: 'frontend', name: t('skills.categories.frontend') },
        { id: 'backend', name: t('skills.categories.backend') },
        { id: 'database', name: t('skills.categories.database') },
        { id: 'other', name: t('skills.categories.other') }
    ];

    const getFilteredSkills = () => {
        if (activeCategory === 'all') {
            return Object.entries(skillsData).flatMap(([_, skills]) => skills);
        }
        return skillsData[activeCategory] || [];
    };

    const filteredSkills = getFilteredSkills();

    const progressBarVariants = {
        hidden: { width: 0 },
        visible: level => ({
            width: `${level}%`,
            transition: { duration: 1, ease: "easeOut" }
        })
    };

    return (
        <section id="skills" className={`py-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 shadow-lg`}>
            <h2 className={`text-3xl font-bold mb-8 text-center ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                {t('skills.title')}
            </h2>

            {/* Dropdown selector para categorías */}
            <div className="flex justify-center mb-8">
                <div className="relative w-full max-w-xs">
                    <select
                        value={activeCategory}
                        onChange={(e) => setActiveCategory(e.target.value)}
                        className={`w-full px-4 py-3 rounded-lg text-sm font-medium transition-all appearance-none cursor-pointer ${
                            darkMode
                                ? 'bg-gray-700 text-gray-200 border-2 border-gray-600 hover:border-cyan-500 focus:border-cyan-500'
                                : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-cyan-500 focus:border-cyan-500'
                        } focus:outline-none focus:ring-2 focus:ring-cyan-500/50`}
                    >
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                    <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                        darkMode ? 'text-cyan-400' : 'text-cyan-600'
                    }`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredSkills.map((skill, index) => (
                    <motion.div
                        key={`${skill.name}-${index}`}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                    >
                        <div className="flex items-center mb-2">
                            {skill.icon && (
                                <span className={`text-2xl mr-2 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                                    {skill.icon}
                                </span>
                            )}
                            <h3 className="text-lg font-semibold">{skill.name}</h3>
                            <span className={`ml-auto text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {skill.level}%
                            </span>
                        </div>

                        <div className={`h-2 w-full rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                            <motion.div
                                custom={skill.level}
                                variants={progressBarVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                className={`h-2 rounded-full ${
                                    skill.level > 80
                                        ? darkMode ? 'bg-green-400' : 'bg-green-500'
                                        : skill.level > 60
                                            ? darkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                                            : darkMode ? 'bg-yellow-400' : 'bg-yellow-500'
                                }`}
                            />
                        </div>
                    </motion.div>
                ))}
            </div>

            <div className="mt-8 text-center">
                <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('skills.learning')}
                </p>
            </div>
        </section>
    );
};

export default Skills;